import { forwardRef } from 'preact/compat'
import { BRAND_CLASSES } from '@/constants/theme'
import { getFieldLabelClasses, getInputSizing, type LabelSize, type ControlDensity } from './formLabelUtils'

export interface SelectOption {
  /** Value for the option */
  value: string | number
  /** Display label for the option */
  label: string
  /** Whether this option is disabled */
  disabled?: boolean
}

export interface SelectProps {
  /** Field label */
  label?: string
  /** Whether field is required */
  required?: boolean
  /** Field name for form registration */
  name: string
  /** Error message to display */
  error?: string
  /** Helper text to display below select */
  helperText?: string
  /** Options to display in the dropdown */
  options: SelectOption[]
  /** Placeholder text for the empty/disabled option */
  placeholder?: string
  /** Custom class name for container */
  containerClassName?: string
  /** Label size variant */
  labelSize?: LabelSize
  /** Field density: 'compact' tightens padding/radius/text for panel-style forms. */
  density?: ControlDensity
  /** Custom class name for label */
  labelClassName?: string
  /** Custom class name for select */
  className?: string
  /** onChange handler */
  onChange?: (e: Event) => void
  /** onBlur handler */
  onBlur?: (e: Event) => void
  /** Default value */
  defaultValue?: string
  /** Additional props passed to select */
  [key: string]: unknown
}

/**
 * Select/Dropdown component with React Hook Form integration
 * 
 * @example
 * // Basic usage with React Hook Form
 * const { register, formState: { errors } } = useForm()
 * 
 * <Select
 *   label="Type"
 *   required
 *   error={errors.type?.message}
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' },
 *   ]}
 *   placeholder="Select a type"
 *   {...register('type', { required: 'Type is required' })}
 * />
 * 
 * @example
 * // With options from lookup
 * const webTypes = getWebTypes()
 * const options = webTypes.map(t => ({ value: t.Key, label: t.Description }))
 * 
 * <Select
 *   label="Web Type"
 *   required
 *   options={options}
 *   {...register('type', { required: 'Required' })}
 * />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      required = false,
      name,
      error,
      helperText,
      options,
      placeholder = 'Select an option',
      className = '',
      containerClassName = '',
      labelSize = 'sm',
      density = 'comfortable',
      labelClassName = '',
      onChange,
      onBlur,
      defaultValue = '',
      ...props
    },
    ref
  ) => {
    const hasError = !!error
    const selectClasses = `w-full border ${getInputSizing(density)} focus:outline-none focus:ring-2 focus:border-transparent ${
      hasError
        ? `${BRAND_CLASSES.INPUT.ERROR}`
        : `${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`
    } ${className}`

    // Treat the select as controlled only when `value` is explicitly defined. Using
    // `'value' in props` would also catch `value={undefined}` (common with optional fields),
    // suppressing `defaultValue` and stranding the select on the empty option.
    const isControlled = (props as { value?: unknown }).value !== undefined

    return (
      <div className={containerClassName}>
        {label && (
          <label
            htmlFor={name}
            className={getFieldLabelClasses(density, labelSize, labelClassName)}
          >
            {label}
            {required && <span className={`${BRAND_CLASSES.INPUT.REQUIRED} ml-1`}>*</span>}
          </label>
        )}

        <select
          id={name}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          className={selectClasses}
          {...(isControlled ? {} : { defaultValue })}
          ref={ref as React.RefObject<HTMLSelectElement>}
          {...props}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {error && (
          <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{error}</p>
        )}

        {helperText && !error && (
          <p className={`mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>{helperText}</p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'
